import React, { useState } from 'react';
import axios from 'axios';
import {
  Paper,
  Box,
  Alert,
  AlertTitle,
  TextField,
  Button,
  Typography,
  CircularProgress,
  Divider,
  Link,
  InputAdornment,
  IconButton
} from '@mui/material';
import { Visibility, VisibilityOff, Email, Lock } from '@mui/icons-material';
import { EMAIL_AUTH_URL } from '../config';
import type { AppUser } from '../types'; // Import shared AppUser type

interface EmailLoginProps {
  onLoginSuccess: (userData: AppUser, isNewUser: boolean, token?: string) => void;
  onLoginError: (errorMessage?: string) => void;
  activeReferralCode?: string | null;
}

const EmailLogin: React.FC<EmailLoginProps> = ({ onLoginSuccess, onLoginError, activeReferralCode }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [stage, setStage] = useState<'email' | 'password' | 'register' | 'verify'>('email');
  const [isResetPassword, setIsResetPassword] = useState(false);

  const handleCheckEmail = async () => {
    if (!email) {
      setError('Please enter your email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${EMAIL_AUTH_URL}/check-email`, { email });
      
      if (response.data.exists) {
        // User exists
        if (!response.data.isEmailVerified) {
          setStage('verify');
          setError('Please verify your email before logging in. Check your email for the verification link.');
        } else if (response.data.hasPassword) {
          setStage('password');
        } else if (response.data.hasGoogleAuth) {
          setError('This account uses Google login. Please sign in with Google.');
        } else if (response.data.setupToken) {
          // User verified email but never set password - redirect to set password
          localStorage.setItem('unitaiAuthToken', response.data.setupToken);
          setSuccess('Email verified! Redirecting to set your password...');
          setTimeout(() => {
            window.location.href = `/set-password?token=${response.data.setupToken}&email=${encodeURIComponent(email)}`;
          }, 1000);
        } else {
          // Shouldn't reach here, but handle gracefully
          setError('Account setup incomplete. Please contact support.');
        }
      } else {
        // New user - show registration form
        setStage('register');
      }
    } catch (error: any) {
      console.error('Email check failed:', error);
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data?.message || 'Failed to check email');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogin = async () => {
    if (!password) {
      setError('Please enter your password');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = { email, password };
      if (activeReferralCode) {
        payload.referralCode = activeReferralCode;
      }

      const response = await axios.post(`${EMAIL_AUTH_URL}/login`, payload);
      
      // Store token in localStorage
      if (response.data.token) {
        localStorage.setItem('unitaiAuthToken', response.data.token);
      }
      
      onLoginSuccess(response.data.user, false, response.data.token);
    } catch (error: any) {
      console.error('Login failed:', error);
      if (axios.isAxiosError(error) && error.response) {
        const message = error.response.data?.message || 'Login failed';
        setError(message);
        
        if (error.response.data?.isEmailVerified === false) {
          setStage('verify');
        }
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleRegister = async () => {
    if (!name || !email) {
      setError('Please enter your name and email');
      return;
    }

    setLoading(true);
    setError(null);

    try {
      const payload: any = { email, name };
      if (activeReferralCode) {
        payload.referralCode = activeReferralCode;
      }

      const response = await axios.post(`${EMAIL_AUTH_URL}/register`, payload);
      
      setSuccess(response.data.message || 'Registration successful! Check your email to verify your account.');
      setStage('verify');
    } catch (error: any) {
      console.error('Registration failed:', error);
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data?.message || 'Registration failed');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${EMAIL_AUTH_URL}/resend-verification`, { email });
      setSuccess(response.data.message || 'Verification email sent!');
    } catch (error: any) {
      console.error('Resend verification failed:', error);
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data?.message || 'Failed to resend verification');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async () => {
    setLoading(true);
    setError(null);

    try {
      const response = await axios.post(`${EMAIL_AUTH_URL}/forgot-password`, { email });
      setSuccess(response.data.message || 'Password reset email sent!');
      setIsResetPassword(false);
    } catch (error: any) {
      console.error('Forgot password failed:', error);
      if (axios.isAxiosError(error) && error.response) {
        setError(error.response.data?.message || 'Failed to send reset email');
      } else {
        setError('Network error. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setStage('email');
    setPassword('');
    setConfirmPassword('');
    setName('');
    setError(null);
    setSuccess(null);
    setIsResetPassword(false);
  };

  return (
    <Paper elevation={3} sx={{ padding: 3, marginTop: 2, maxWidth: 400, margin: '0 auto' }}>
      <Typography variant="h6" gutterBottom align="center">
        {stage === 'register' ? 'Create Account' : 
         stage === 'verify' ? 'Verify Email' :
         isResetPassword ? 'Reset Password' :
         'Sign In'}
      </Typography>
      
      <Box component="form" onSubmit={(e) => { e.preventDefault(); }} sx={{ mt: 2 }}>
        {/* Email Input - Always shown except in verify stage */}
        {stage !== 'verify' && (
          <TextField
            fullWidth
            label="Email"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={stage !== 'email' || loading}
            margin="normal"
            required
            autoFocus={stage === 'email'}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <Email />
                </InputAdornment>
              ),
            }}
          />
        )}

        {/* Registration Name Field */}
        {stage === 'register' && (
          <TextField
            fullWidth
            label="Full Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            disabled={loading}
            margin="normal"
            required
            autoFocus
          />
        )}

        {/* Password Field */}
        {stage === 'password' && (
          <>
            <TextField
              fullWidth
              label="Password"
              type={showPassword ? 'text' : 'password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={loading}
              margin="normal"
              required
              autoFocus
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Lock />
                  </InputAdornment>
                ),
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                      disabled={loading}
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            {!isResetPassword && (
              <Box sx={{ textAlign: 'right', mt: 1 }}>
                <Link
                  component="button"
                  variant="body2"
                  onClick={(e) => {
                    e.preventDefault();
                    setIsResetPassword(true);
                  }}
                  sx={{ cursor: 'pointer' }}
                >
                  Forgot password?
                </Link>
              </Box>
            )}
          </>
        )}

        {/* Verify Stage */}
        {stage === 'verify' && (
          <Alert severity="info" sx={{ mt: 2 }}>
            <AlertTitle>Email Verification Required</AlertTitle>
            Please check your email ({email}) and click the verification link to activate your account.
          </Alert>
        )}

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {error}
          </Alert>
        )}

        {/* Success Alert */}
        {success && (
          <Alert severity="success" sx={{ mt: 2 }}>
            {success}
          </Alert>
        )}

        {/* Action Buttons */}
        <Box sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {stage === 'email' && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleCheckEmail}
              disabled={loading || !email}
              sx={{ height: 48 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Continue'}
            </Button>
          )}

          {stage === 'password' && !isResetPassword && (
            <>
              <Button
                fullWidth
                variant="contained"
                onClick={handleLogin}
                disabled={loading || !password}
                sx={{ height: 48 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Sign In'}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={handleBack}
                disabled={loading}
              >
                Use Different Email
              </Button>
            </>
          )}

          {stage === 'password' && isResetPassword && (
            <>
              <Button
                fullWidth
                variant="contained"
                onClick={handleForgotPassword}
                disabled={loading}
                sx={{ height: 48 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Send Reset Email'}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={() => setIsResetPassword(false)}
                disabled={loading}
              >
                Back to Sign In
              </Button>
            </>
          )}

          {stage === 'register' && (
            <>
              <Button
                fullWidth
                variant="contained"
                onClick={handleRegister}
                disabled={loading || !email || !name}
                sx={{ height: 48 }}
              >
                {loading ? <CircularProgress size={24} /> : 'Create Account'}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={handleBack}
                disabled={loading}
              >
                Back
              </Button>
            </>
          )}

          {stage === 'verify' && (
            <>
              <Button
                fullWidth
                variant="outlined"
                onClick={handleResendVerification}
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Resend Verification Email'}
              </Button>
              <Button
                fullWidth
                variant="text"
                onClick={handleBack}
                disabled={loading}
              >
                Use Different Email
              </Button>
            </>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default EmailLogin;
